import {
  Alert,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  ListSubheader,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { Box } from "@mui/system";
import UploadFile from "@mui/icons-material/UploadFile";
import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getCIDS } from "../services/cidService";
import {
  previewImportService,
  importMappedService,
} from "../services/importService";
import { notifyService } from "../services/notifyService";
import { canWriteCid } from "../utils/permissions";

const errorText = (err, fallback) =>
  typeof err?.response?.data === "string" ? err.response.data : fallback;

// Builds the initial column->target mapping for a sheet from the server's
// suggestions.
const suggestedMapping = (sheet) => {
  const mapping = {};
  (sheet?.headers || []).forEach((h) => {
    mapping[h.index] = h.suggestedTarget || "";
  });
  return mapping;
};

const ImportPage = () => {
  const auth = useSelector((state) => state.auth.value);
  const assessorCid = useSelector((state) => state.assessor.value.form.cid);

  const [cids, setCids] = useState([]);
  const [cid, setCid] = useState("");
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sheetName, setSheetName] = useState("");
  const [mapping, setMapping] = useState({});
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const writableCids = useMemo(
    () => cids.filter((c) => canWriteCid(auth, c.id)),
    [cids, auth]
  );

  useEffect(() => {
    getCIDS().then((list) => {
      if (list) {
        setCids(list);
      }
    });
  }, []);

  // Carry the Quick Assessor's current CID over as the default target.
  useEffect(() => {
    if (!cid && assessorCid && writableCids.some((c) => c.id === assessorCid)) {
      setCid(assessorCid);
    }
  }, [assessorCid, writableCids, cid]);

  const sheet = useMemo(
    () => preview?.sheets?.find((s) => s.name === sheetName) || null,
    [preview, sheetName]
  );

  const mappedTargets = Object.values(mapping).filter(Boolean);
  const parcelMapped = mappedTargets.includes("parcelID");
  const duplicateTargets = useMemo(() => {
    const counts = {};
    mappedTargets.forEach((t) => {
      counts[t] = (counts[t] || 0) + 1;
    });
    return Object.keys(counts).filter((t) => counts[t] > 1);
  }, [mappedTargets]);

  const targetLabels = useMemo(() => {
    const labels = {};
    (preview?.targets?.standard || []).forEach((t) => {
      labels[t.key] = t.label;
    });
    (preview?.targets?.custom || []).forEach((t) => {
      labels[t.key] = t.label;
    });
    return labels;
  }, [preview]);

  const loadFile = async (nextFile) => {
    if (!nextFile) {
      return;
    }
    setFile(nextFile);
    setPreview(null);
    setResult(null);
    setSheetName("");
    setMapping({});
    setPreviewLoading(true);
    try {
      const data = await previewImportService(nextFile);
      setPreview(data);
      const firstWithRows = data.sheets?.find((s) => s.rowCount > 0);
      if (firstWithRows) {
        setSheetName(firstWithRows.name);
        setMapping(suggestedMapping(firstWithRows));
      }
    } catch (err) {
      setFile(null);
      notifyService(errorText(err, "Could not read that file"));
    } finally {
      setPreviewLoading(false);
    }
  };

  const selectSheet = (name) => {
    setSheetName(name);
    setResult(null);
    setMapping(suggestedMapping(preview?.sheets?.find((s) => s.name === name)));
  };

  const setColumnTarget = (index, target) => {
    setMapping((prev) => ({ ...prev, [index]: target }));
  };

  const handleImport = async () => {
    setImporting(true);
    setResult(null);
    try {
      const mappingArray = Object.entries(mapping)
        .filter(([, target]) => target)
        .map(([index, target]) => ({ index: parseInt(index, 10), target }));
      const data = await importMappedService(cid, file, sheetName, mappingArray);
      setResult({ ...data, sheetName });
      notifyService("Import complete");
    } catch (err) {
      notifyService(errorText(err, "Import failed"));
    } finally {
      setImporting(false);
    }
  };

  const handleTemplateDownload = () => {
    const link = document.createElement("a");
    link.href = `${process.env.PUBLIC_URL || ""}/uploadTemplate.csv`;
    link.download = "uploadTemplate.csv";
    link.click();
  };

  // Sample values shown beside each source column so the user can tell messy
  // columns apart (e.g. two "Description" headers).
  const sampleFor = (index) => {
    const values = (sheet?.sample || [])
      .map((row) => row[index])
      .filter((v) => v !== undefined && v !== null && String(v).trim() !== "")
      .slice(0, 2)
      .join(" · ");
    return values.length > 70 ? `${values.slice(0, 70)}…` : values;
  };

  return (
    <Grid container sx={{ p: { xs: 1, md: 2 } }} spacing={2}>
      <Grid item xs={12}>
        <Card variant="outlined">
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={1}
            >
              <Stack spacing={0.5}>
                <Typography variant="h5">Import</Typography>
                <Typography variant="body2" color="text.secondary">
                  Upload a spreadsheet (.xlsx, .xls, or .csv), match its columns
                  to CRM fields, and import it into a CID — no need to reformat
                  the file first.
                </Typography>
              </Stack>
              <Button variant="outlined" onClick={handleTemplateDownload}>
                Download CSV Template
              </Button>
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      {!writableCids.length && cids.length ? (
        <Grid item xs={12}>
          <Alert severity="info">
            You don't have write access to any CID, so you can't import. Ask an
            admin for write permissions.
          </Alert>
        </Grid>
      ) : null}

      <Grid item xs={12} md={4}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Typography variant="h6">1. Choose CID &amp; file</Typography>
              <FormControl fullWidth size="small">
                <InputLabel>Import into CID</InputLabel>
                <Select
                  label="Import into CID"
                  value={writableCids.some((c) => c.id === cid) ? cid : ""}
                  onChange={(e) => setCid(e.target.value)}
                >
                  {writableCids.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box
                onClick={() => fileInputRef.current?.click()}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragging(true);
                }}
                onDragLeave={() => setDragging(false)}
                onDrop={(e) => {
                  e.preventDefault();
                  setDragging(false);
                  loadFile(e.dataTransfer.files?.[0]);
                }}
                sx={{
                  backgroundColor: dragging ? "#0026ff" : "#ececec",
                  color: dragging ? "white" : "black",
                  minHeight: 140,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  textAlign: "center",
                  borderRadius: "10px",
                  cursor: "pointer",
                  p: 2,
                }}
              >
                <UploadFile />
                {file ? (
                  <Typography variant="body2">{file.name}</Typography>
                ) : (
                  <Typography variant="body2">
                    Click or drag a .xlsx / .xls / .csv file here
                  </Typography>
                )}
                <input
                  style={{ display: "none" }}
                  type="file"
                  accept=".csv,.xlsx,.xls"
                  ref={fileInputRef}
                  onChange={(e) => {
                    loadFile(e.target.files?.[0]);
                    e.target.value = "";
                  }}
                />
              </Box>

              {previewLoading ? (
                <Stack direction="row" spacing={1} alignItems="center">
                  <CircularProgress size={18} />
                  <Typography variant="body2">Reading file…</Typography>
                </Stack>
              ) : null}

              {preview?.sheets?.length ? (
                <FormControl fullWidth size="small">
                  <InputLabel>Sheet</InputLabel>
                  <Select
                    label="Sheet"
                    value={sheetName}
                    onChange={(e) => selectSheet(e.target.value)}
                  >
                    {preview.sheets.map((s) => (
                      <MenuItem key={s.name} value={s.name} disabled={!s.rowCount}>
                        {s.name} ({s.rowCount} rows)
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              ) : null}
            </Stack>
          </CardContent>
        </Card>
      </Grid>

      <Grid item xs={12} md={8}>
        <Card variant="outlined">
          <CardContent>
            <Stack spacing={2}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                flexWrap="wrap"
              >
                <Typography variant="h6">2. Map columns</Typography>
                {sheet ? (
                  <Chip
                    size="small"
                    label={`${sheet.rowCount} rows in "${sheet.name}"`}
                  />
                ) : null}
              </Stack>

              {!sheet ? (
                <Typography variant="body2" color="text.secondary">
                  Upload a file to see its columns here. Columns are matched to
                  CRM fields automatically where possible — you confirm or
                  adjust each one before importing.
                </Typography>
              ) : (
                <>
                  {!parcelMapped ? (
                    <Alert severity="warning">
                      Map a column to <strong>Parcel ID</strong> — it's how rows
                      are matched to properties.
                    </Alert>
                  ) : null}
                  {duplicateTargets.length ? (
                    <Alert severity="info">
                      Multiple columns map to{" "}
                      {duplicateTargets
                        .map((t) => targetLabels[t] || t)
                        .join(", ")}
                      ; the first non-empty value in each row wins.
                    </Alert>
                  ) : null}

                  <TableContainer sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>Source column</TableCell>
                          <TableCell>Sample values</TableCell>
                          <TableCell sx={{ minWidth: 220 }}>
                            Import as
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sheet.headers.map((h) => (
                          <TableRow key={h.index} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {h.name || `Column ${h.index + 1}`}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {sampleFor(h.index)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Select
                                size="small"
                                fullWidth
                                value={mapping[h.index] || ""}
                                onChange={(e) =>
                                  setColumnTarget(h.index, e.target.value)
                                }
                                displayEmpty
                              >
                                <MenuItem value="">
                                  <em>Skip this column</em>
                                </MenuItem>
                                <ListSubheader>Property fields</ListSubheader>
                                {(preview.targets?.standard || [])
                                  .filter((t) => t.entity === "property")
                                  .map((t) => (
                                    <MenuItem key={t.key} value={t.key}>
                                      {t.label}
                                    </MenuItem>
                                  ))}
                                <ListSubheader>Contact fields</ListSubheader>
                                {(preview.targets?.standard || [])
                                  .filter((t) => t.entity === "contact")
                                  .map((t) => (
                                    <MenuItem key={t.key} value={t.key}>
                                      {t.label}
                                    </MenuItem>
                                  ))}
                                {(preview.targets?.custom || []).length ? (
                                  <ListSubheader>Custom fields</ListSubheader>
                                ) : null}
                                {(preview.targets?.custom || []).map((t) => (
                                  <MenuItem key={t.key} value={t.key}>
                                    {t.label} (
                                    {t.entity === "property"
                                      ? "Property"
                                      : "Contact"}
                                    )
                                  </MenuItem>
                                ))}
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Stack
                    direction="row"
                    spacing={2}
                    alignItems="center"
                    justifyContent="flex-end"
                  >
                    {importing ? <CircularProgress size={20} /> : null}
                    <Button
                      variant="contained"
                      disabled={
                        !cid || !sheet || !parcelMapped || importing
                      }
                      onClick={handleImport}
                    >
                      Import {sheet.rowCount} rows
                    </Button>
                  </Stack>

                  {result ? (
                    <Alert severity="success">
                      Imported sheet "{result.sheetName}":{" "}
                      {result.summary?.propertiesCreated ?? 0} properties
                      created, {result.summary?.propertiesUpdated ?? 0} updated;{" "}
                      {result.summary?.contactsCreated ?? 0} contacts created,{" "}
                      {result.summary?.contactsUpdated ?? 0} enriched.
                      {result.rowsSkipped
                        ? ` ${result.rowsSkipped} rows skipped (no usable Parcel ID).`
                        : ""}{" "}
                      Pick another sheet to keep going.
                    </Alert>
                  ) : null}
                </>
              )}
            </Stack>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export default ImportPage;
